import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationsAPI } from '../../services/api';
import { includesNormalized } from '../../utils/searchUtils';
import './Notifications.css';

const Notifications = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getAll({ limit: 200 });
      setItems(response.notifications || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleRead = async (id) => {
    await notificationsAPI.markRead(id);
    await loadItems();
  };

  const availableTypes = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.type).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nl'));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (readFilter === 'read' && !item.isRead) return false;
      if (readFilter === 'unread' && item.isRead) return false;

      if (query) {
        const haystack = [item.title, item.message, item.type, item.channels?.join(' '), item.recipient?.name].join(' ');
        if (!includesNormalized(haystack, query)) return false;
      }

      return true;
    });
  }, [items, query, readFilter, typeFilter]);

  const unreadCount = useMemo(() => items.filter((item) => !item.isRead).length, [items]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="student-dashboard student-notifications-page">
      <div className="dashboard-header">
        <h1>{t('notification.title')}</h1>
        <span className="student-notification-counter">{unreadCount} ongelezen</span>
      </div>

      <div className="student-notification-filters">
        <input
          type="search"
          className="form-input"
          placeholder="Zoek op titel, bericht of kanaal"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select className="form-input" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">Alle types</option>
          {availableTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <select className="form-input" value={readFilter} onChange={(event) => setReadFilter(event.target.value)}>
          <option value="all">Alles</option>
          <option value="unread">Alleen ongelezen</option>
          <option value="read">Alleen gelezen</option>
        </select>
      </div>

      <div className="student-notification-list">
        {loading && <p className="student-notification-empty">Meldingen laden...</p>}
        {!loading && filteredItems.length === 0 && <p className="student-notification-empty">Geen meldingen gevonden voor deze filters.</p>}
        {!loading && filteredItems.map((item) => (
          <article key={item.id} className={`student-notification-item ${item.isRead ? 'read' : 'unread'}`}>
            <div className="student-notification-head">
              <h3>{item.title}</h3>
              <span className={`student-notification-status ${item.isRead ? 'read' : 'unread'}`}>
                {item.isRead ? 'Gelezen' : 'Nieuw'}
              </span>
            </div>
            <p>{item.message}</p>
            <div className="student-notification-meta">
              <span>Type: {item.type}</span>
              <span>Kanalen: {(item.channels || []).join(', ') || '-'}</span>
              <span>Aangemaakt: {formatDateTime(item.createdAt)}</span>
            </div>
            {!item.isRead && (
              <div className="form-actions" style={{ marginTop: '10px' }}>
                <button className="btn-primary" onClick={() => handleRead(item.id)}>{t('notification.markRead')}</button>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
};

export default Notifications;
